import React, { useEffect } from "react";
import T from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { toastr } from "react-redux-toastr";
import { matchesBoolean, matchesString } from "LIB/filters";
import { SUCCESS } from "LIB/submission";
import {
  FilterSelect,
  Pagination,
  SearchFilter,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import { Button, Search } from "MODULES/shared/components";
import { stringify } from "query-string";
import { renderIf } from "MODULES/shared/decorators";
import api from "APP_ROOT/api";
import useCurrentUser from "APP_ROOT/hooks/useCurrentUser";
import { API_BASE, CLEAR_FILTERS_TEXT } from "APP_ROOT/constants";
import {
  ADMIN,
  EXPORT_LIMIT,
  WAREHOUSE_LOGISTICS_DIRECTOR,
  WAREHOUSE_MANAGER
} from "MODULES/users/constants";
import { STORE_LOCATION_ID } from "MODULES/inventories/constants";
import { hasRole, hasSubRole } from "MODULES/users/helpers";
import Stats from "MODULES/OutgoingShipments/components/Search/Stats";
import DateRangePicker from "MODULES/shared/components/DateRangePicker/DateRangePicker";
import { subscribe, unsubscribe } from "MODULES/OutgoingShipments/actions";
import Table from "./Table";
import { shipmentsPagination } from "../../pagination";

const defaultFilters = {
  checked: undefined,
  dropshipped: undefined,
  end_date: undefined,
  source: undefined,
  start_date: undefined,
  [STORE_LOCATION_ID]: undefined,
  transfer: undefined
};

const filters = [
  {
    label: "Checked",
    filters: { checked: true }
  },
  {
    label: "Unchecked",
    filters: { checked: false }
  },
  {
    label: "Dropshipped",
    filters: { dropshipped: true }
  },
  {
    label: "SOs",
    filters: { source: "SalesOrderLineItem" }
  },
  {
    label: "ROs",
    filters: { source: "RepairOrderLineItem" },
    hr: true
  },
  {
    label: "All Type 'TR'",
    filters: { transfer: true },
    withAuthorization: {
      authorizedIf: user =>
        hasSubRole(user.toJS(), [
          WAREHOUSE_LOGISTICS_DIRECTOR,
          WAREHOUSE_MANAGER
        ])
    }
  }
];

const filterTags = [
  {
    label: "Checked",
    key: "checked",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Unchecked",
    key: "checked",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Transfers",
    key: "transfer"
  },
  {
    label: "Dropshipped",
    key: "dropshipped"
  },
  {
    label: "SOs",
    key: "source",
    matchesTarget: matchesString("SalesOrderLineItem")
  },
  {
    label: "ROs",
    key: "source",
    matchesTarget: matchesString("RepairOrderLineItem")
  }
];

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const totalCount = searchProps.paginator.get("totalCount");

  params.total_count = totalCount;
  return (
    <Button
      onClick={() => {
        if (totalCount > EXPORT_LIMIT) {
          toastr.info("Processing Report.");
          api.shipments.export(params).then(() => SUCCESS);
        } else {
          window.open(
            `${API_BASE}/shipments/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Shipped Orders Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

ExportButton.propTypes = {
  searchProps: T.shape({
    paginator: T.instanceOf(Map)
  })
};

const meetsExportCriteria = renderIf(({ searchProps }) => {
  const user = useCurrentUser();
  return searchProps.paginator.get("totalCount") <= 450 || hasRole(user, ADMIN);
});

const DecoratedExportButton = meetsExportCriteria(ExportButton);

function ShipmentsSearch({
  pushHistory,
  subscribeToShipmentsChannel,
  unsubscribeToShipmentsChannel
}) {
  const currentUser = useCurrentUser();

  const filterTagsWithLocation = currentUser.locations.map(l => ({
    label: `From ${l.name}`,
    key: STORE_LOCATION_ID,
    matchesTarget: matchesString(l.id)
  }));

  const filtersWithLocations =
    currentUser.locations.length > 1
      ? currentUser.locations.map((l, i) => ({
          label: `From ${l.name}`,
          filters: { [STORE_LOCATION_ID]: l.id },
          hr: currentUser.locations.length - 1 === i
        }))
      : null;

  useEffect(() => {
    subscribeToShipmentsChannel();

    return function cleanup() {
      unsubscribeToShipmentsChannel();
    };
  }, []);

  return (
    <Search search={shipmentsPagination} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing a part number, awb number, company name, ship date, or any reference number..."
                submitOnSingleResult={r => pushHistory(`/shipping/${r.id}`)}
              />
              <DecoratedExportButton searchProps={searchProps} />
              <DateRangePicker {...searchProps} />
              <FilterSelect
                {...searchProps}
                filters={filters.concat(filtersWithLocations || []).concat({
                  label: CLEAR_FILTERS_TEXT,
                  filters: { ...defaultFilters }
                })}
                right
              />
            </div>
          </div>
          <Stats />
          <TitleWithFilters
            {...searchProps}
            title="Shipped Orders"
            filters={filterTags.concat(filterTagsWithLocation)}
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}

ShipmentsSearch.propTypes = {
  pushHistory: T.func.isRequired
};

export default connect(undefined, dispatch => ({
  subscribeToShipmentsChannel: () => dispatch(subscribe()),
  unsubscribeToShipmentsChannel: () => dispatch(unsubscribe()),
  pushHistory: path => dispatch(push(path))
}))(ShipmentsSearch);



// WEBPACK FOOTER //
// ./app/modules/OutgoingShipments/components/Search/Search.jsx